import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { MdSearch, MdRefresh, MdDownload, MdExpandMore, MdChevronRight } from 'react-icons/md';
import CompaniesApi from '../../../../api/companies';
import EmployeeStatusDrilldownModal from './EmployeeStatusDrilldownModal';

const num = (v) => (v == null ? '—' : Number(v).toLocaleString('he-IL'));
const pct = (a, b) => (b && b > 0 ? Math.round((a / b) * 100) : null);

function DeltaBadge({ value }) {
  if (value == null) return <Text as="span" color="gray.400" fontSize="xs">—</Text>;
  if (value === 0) return <Text as="span" color="gray.500" fontSize="xs">0</Text>;
  const sign = value > 0 ? '+' : '';
  const color = value > 0 ? 'green.500' : 'red.500';
  return (
    <Text as="span" color={color} fontSize="xs" fontWeight="500">
      {sign}
      {value}
    </Text>
  );
}

function MiniFunnel({ row }) {
  const total = Math.max(row.phonebookCount ?? 0, row.activeEmployees ?? 0, row.registeredEmployees ?? 0, 1);
  const transferring = row.activelyTransferring ?? 0;
  const registeredRest = Math.max((row.registeredEmployees ?? 0) - transferring, 0);
  const activeUnreg = row.activeUnregistered ?? 0;
  const dormant = Math.max(total - transferring - registeredRest - activeUnreg, 0);
  const segments = [
    { v: transferring, color: '#01B574' },
    { v: registeredRest, color: '#9F7AEA' },
    { v: activeUnreg, color: '#F6AD55' },
    { v: dormant, color: '#CBD5E0' },
  ];
  return (
    <Flex h="10px" borderRadius="sm" overflow="hidden" minW="80px">
      {segments.map((s, i) => (
        <Box key={i} flex={s.v / total} bg={s.color} />
      ))}
    </Flex>
  );
}

function toCsv(rows) {
  const headers = [
    'companyId',
    'companyName',
    'organizationName',
    'phonebookCount',
    'activeEmployees',
    'activeUnregistered',
    'registeredEmployees',
    'activelyTransferring',
    'deltaActive7d',
    'deltaRegistered7d',
    'deltaTransferring7d',
    'computedAt',
  ];
  const esc = (v) => {
    if (v == null) return '';
    const s = String(v);
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.join(',')];
  for (const r of rows) lines.push(headers.map((h) => esc(r[h])).join(','));
  return lines.join('\n');
}

function downloadCsv(filename, content) {
  const blob = new Blob(['﻿' + content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function aggregateOrg(rows) {
  // Sum metrics across sub-companies. Phonebook sum is approximate (slight overlap for group vaults).
  return rows.reduce(
    (acc, r) => ({
      phonebookCount: (acc.phonebookCount ?? 0) + (r.phonebookCount ?? 0),
      activeEmployees: (acc.activeEmployees ?? 0) + (r.activeEmployees ?? 0),
      activeUnregistered:
        r.activeUnregistered == null && acc.activeUnregistered == null
          ? null
          : (acc.activeUnregistered ?? 0) + (r.activeUnregistered ?? 0),
      registeredEmployees: (acc.registeredEmployees ?? 0) + (r.registeredEmployees ?? 0),
      activelyTransferring: (acc.activelyTransferring ?? 0) + (r.activelyTransferring ?? 0),
      deltaActive7d: addDelta(acc.deltaActive7d, r.deltaActive7d),
      deltaRegistered7d: addDelta(acc.deltaRegistered7d, r.deltaRegistered7d),
      deltaTransferring7d: addDelta(acc.deltaTransferring7d, r.deltaTransferring7d),
    }),
    {
      phonebookCount: 0,
      activeEmployees: 0,
      activeUnregistered: null,
      registeredEmployees: 0,
      activelyTransferring: 0,
      deltaActive7d: null,
      deltaRegistered7d: null,
      deltaTransferring7d: null,
    }
  );
}

function addDelta(a, b) {
  if (a == null && b == null) return null;
  return (a ?? 0) + (b ?? 0);
}

export default function EmployeeStatusTable() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [recomputing, setRecomputing] = useState(false);
  const [sortKey, setSortKey] = useState('activeUnregistered');
  const [sortDir, setSortDir] = useState('desc');
  const [groupByOrg, setGroupByOrg] = useState(true);
  const [expandedOrgs, setExpandedOrgs] = useState({});
  const [selectedCompany, setSelectedCompany] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const headerBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const orgRowBg = useColorModeValue('blue.50', 'blue.900');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const hoverBg = useColorModeValue('gray.100', 'whiteAlpha.200');

  const load = async () => {
    setLoading(true);
    try {
      const res = await CompaniesApi.getStats();
      setRows(Array.isArray(res) ? res : res?.data ?? []);
    } catch (err) {
      toast({ status: 'error', title: 'שגיאה בטעינת הנתונים', description: err?.message || String(err) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleRecompute = async () => {
    setRecomputing(true);
    try {
      await CompaniesApi.recomputeStats();
      toast({ status: 'success', title: 'חישוב מחדש הופעל' });
      await load();
    } catch (err) {
      toast({ status: 'error', title: 'שגיאה', description: err?.message || String(err) });
    } finally {
      setRecomputing(false);
    }
  };

  const openDrilldown = (row) => {
    setSelectedCompany(row);
    onOpen();
  };

  const filtered = useMemo(() => {
    const q = search.trim();
    const list = q
      ? rows.filter(
          (r) =>
            (r.companyName || '').includes(q) ||
            String(r.companyId).includes(q) ||
            (r.organizationName || '').includes(q)
        )
      : rows;
    const sorted = [...list].sort((a, b) => {
      const av = a[sortKey] ?? -1;
      const bv = b[sortKey] ?? -1;
      if (typeof av === 'string' || typeof bv === 'string') {
        return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
      }
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return sorted;
  }, [rows, search, sortKey, sortDir]);

  // Build display list: either flat, or org-grouped with rollup rows.
  const display = useMemo(() => {
    if (!groupByOrg) return filtered.map((r) => ({ type: 'co', row: r }));

    const byOrg = new Map();
    const orphans = [];
    for (const r of filtered) {
      if (r.organizationId == null) {
        orphans.push(r);
      } else {
        if (!byOrg.has(r.organizationId)) byOrg.set(r.organizationId, []);
        byOrg.get(r.organizationId).push(r);
      }
    }

    const out = [];
    for (const [orgId, subs] of byOrg.entries()) {
      if (subs.length < 2) {
        // Not really a group — show as plain co
        subs.forEach((r) => out.push({ type: 'co', row: r }));
        continue;
      }
      const rollup = aggregateOrg(subs);
      const orgName = subs[0].organizationName || `Org #${orgId}`;
      out.push({ type: 'org', orgId, orgName, rollup, subs });
      if (expandedOrgs[orgId]) {
        subs.forEach((r) => out.push({ type: 'co', row: r, inOrg: true }));
      }
    }
    orphans.forEach((r) => out.push({ type: 'co', row: r }));
    return out;
  }, [filtered, groupByOrg, expandedOrgs]);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, r) => ({
        companies: acc.companies + 1,
        phonebook: acc.phonebook + (r.phonebookCount || 0),
        active: acc.active + (r.activeEmployees || 0),
        activeUnreg: acc.activeUnreg + (r.activeUnregistered || 0),
        registered: acc.registered + (r.registeredEmployees || 0),
        transferring: acc.transferring + (r.activelyTransferring || 0),
      }),
      { companies: 0, phonebook: 0, active: 0, activeUnreg: 0, registered: 0, transferring: 0 }
    );
  }, [rows]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const toggleOrg = (orgId) => {
    setExpandedOrgs((prev) => ({ ...prev, [orgId]: !prev[orgId] }));
  };

  const headerCell = (label, key, help) => (
    <Th textAlign="center" cursor="pointer" onClick={() => toggleSort(key)} _hover={{ bg: headerBg }}>
      <Tooltip label={help} placement="top" hasArrow>
        <Text as="span">
          {label}
          {sortKey === key && (sortDir === 'desc' ? ' ↓' : ' ↑')}
        </Text>
      </Tooltip>
    </Th>
  );

  const renderMetricCells = (r, { isOrgRow = false } = {}) => {
    const transferRate = pct(r.activelyTransferring, r.registeredEmployees);
    return (
      <>
        <Td textAlign="center">{num(r.phonebookCount)}</Td>
        <Td textAlign="center">
          <HStack justify="center" spacing={1}>
            <Text>{num(r.registeredEmployees)}</Text>
            {r.deltaRegistered7d != null && (
              <Text fontSize="xs">
                (<DeltaBadge value={r.deltaRegistered7d} />)
              </Text>
            )}
          </HStack>
        </Td>
        <Td textAlign="center">
          <HStack justify="center" spacing={1}>
            <Text>{num(r.activeEmployees)}</Text>
            {r.deltaActive7d != null && (
              <Text fontSize="xs">
                (<DeltaBadge value={r.deltaActive7d} />)
              </Text>
            )}
          </HStack>
        </Td>
        <Td textAlign="center">
          <HStack justify="center" spacing={1}>
            <Text>{num(r.activelyTransferring)}</Text>
            {r.deltaTransferring7d != null && (
              <Text fontSize="xs">
                (<DeltaBadge value={r.deltaTransferring7d} />)
              </Text>
            )}
          </HStack>
        </Td>
        <Td textAlign="center">
          {r.activeUnregistered == null ? (
            <Text color="gray.400">—</Text>
          ) : r.activeUnregistered > 0 ? (
            <Badge colorScheme="orange" variant={isOrgRow ? 'solid' : 'subtle'}>
              {num(r.activeUnregistered)} פוטנציאל
            </Badge>
          ) : (
            <Text>0</Text>
          )}
        </Td>
        <Td textAlign="center" minW="100px">
          <MiniFunnel row={r} />
        </Td>
        <Td textAlign="center">
          {transferRate == null ? (
            <Text color="gray.400">—</Text>
          ) : (
            <Badge colorScheme={transferRate >= 50 ? 'green' : transferRate >= 20 ? 'yellow' : 'red'}>
              {transferRate}%
            </Badge>
          )}
        </Td>
      </>
    );
  };

  return (
    <Box pt={4}>
      <Flex gap={3} mb={4} wrap="wrap" direction={{ base: 'column', md: 'row' }}>
        <Stat flex={1} minW="130px" bg={headerBg} p={3} borderRadius="md">
          <StatLabel>חברות</StatLabel>
          <StatNumber>{totals.companies}</StatNumber>
        </Stat>
        <Stat flex={1} minW="130px" bg={headerBg} p={3} borderRadius="md">
          <StatLabel>סה"כ באלפון</StatLabel>
          <StatNumber>{totals.phonebook.toLocaleString('he-IL')}</StatNumber>
        </Stat>
        <Stat flex={1} minW="130px" bg={headerBg} p={3} borderRadius="md">
          <StatLabel>רשומים</StatLabel>
          <StatNumber>{totals.registered.toLocaleString('he-IL')}</StatNumber>
        </Stat>
        <Stat flex={1} minW="130px" bg={headerBg} p={3} borderRadius="md">
          <StatLabel>פעילים רשומים</StatLabel>
          <StatNumber>{totals.active.toLocaleString('he-IL')}</StatNumber>
          <StatHelpText>משמרת ב-60 יום</StatHelpText>
        </Stat>
        <Stat flex={1} minW="130px" bg={headerBg} p={3} borderRadius="md">
          <StatLabel>ביצעו העברה</StatLabel>
          <StatNumber color="green.500">{totals.transferring.toLocaleString('he-IL')}</StatNumber>
        </Stat>
        <Stat flex={1} minW="130px" bg={headerBg} p={3} borderRadius="md">
          <StatLabel>פעילים לא רשומים</StatLabel>
          <StatNumber color="orange.500">{totals.activeUnreg.toLocaleString('he-IL')}</StatNumber>
          <StatHelpText>הזדמנות אונבורדינג</StatHelpText>
        </Stat>
      </Flex>

      <Flex gap={2} mb={3} align="center" wrap="wrap">
        <InputGroup maxW="320px">
          <InputLeftElement pointerEvents="none">
            <Icon as={MdSearch} />
          </InputLeftElement>
          <Input
            placeholder="חיפוש לפי שם חברה, מזהה, או ארגון"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </InputGroup>
        <HStack>
          <Text fontSize="sm">קיבוץ לפי ארגון</Text>
          <Switch isChecked={groupByOrg} onChange={(e) => setGroupByOrg(e.target.checked)} />
        </HStack>
        <Button
          leftIcon={<Icon as={MdRefresh} />}
          onClick={handleRecompute}
          isLoading={recomputing}
          size="sm"
          variant="outline"
        >
          חשב מחדש
        </Button>
        <Button
          leftIcon={<Icon as={MdDownload} />}
          onClick={() => downloadCsv(`employee-status-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(rows))}
          size="sm"
          variant="outline"
        >
          CSV
        </Button>
        <Text fontSize="sm" color="gray.500">
          {filtered.length} מתוך {rows.length}
        </Text>
      </Flex>

      {loading ? (
        <Flex justify="center" py={10}>
          <Spinner />
        </Flex>
      ) : (
        <Box borderWidth={1} borderColor={borderColor} borderRadius="md" overflowX="auto">
          <Table size="sm" variant="simple">
            <Thead bg={headerBg}>
              <Tr>
                {headerCell('חברה', 'companyName', 'שם החברה או ארגון')}
                {headerCell('סה"כ באלפון', 'phonebookCount', 'מספר רשומות באלפון המוצפן')}
                {headerCell('רשומים', 'registeredEmployees', 'משתמשים רשומים (type=2)')}
                {headerCell('פעילים רשומים', 'activeEmployees', 'עובדים רשומים עם ≥1 משמרת ב-60 יום (מ-time_reporting_details)')}
                {headerCell('ביצעו העברה', 'activelyTransferring', 'משתמשים שביצעו ≥1 העברה מוצלחת ב-60 יום')}
                {headerCell('פעילים לא רשומים', 'activeUnregistered', 'עובדים עם משמרת שעדיין לא נרשמו — הזדמנות אונבורדינג. מוצג "—" אם ספק המערכת אינו נתמך עדיין')}
                <Th textAlign="center">
                  <Tooltip label="transferring / registered / active-unregistered / dormant" hasArrow>
                    <Text as="span">פאנל</Text>
                  </Tooltip>
                </Th>
                <Th textAlign="center">אחוז משיכה</Th>
              </Tr>
            </Thead>
            <Tbody>
              {display.map((item, idx) => {
                if (item.type === 'org') {
                  const { orgId, orgName, rollup, subs } = item;
                  const isOpen = !!expandedOrgs[orgId];
                  return (
                    <Tr
                      key={`org-${orgId}`}
                      bg={orgRowBg}
                      cursor="pointer"
                      onClick={() => toggleOrg(orgId)}
                    >
                      <Td>
                        <HStack spacing={1}>
                          <IconButton
                            size="xs"
                            variant="ghost"
                            aria-label="toggle"
                            icon={<Icon as={isOpen ? MdExpandMore : MdChevronRight} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleOrg(orgId);
                            }}
                          />
                          <Box>
                            <Text fontWeight="700">{orgName}</Text>
                            <Text fontSize="xs" color="gray.500">
                              {subs.length} חברות
                            </Text>
                          </Box>
                        </HStack>
                      </Td>
                      {renderMetricCells(rollup, { isOrgRow: true })}
                    </Tr>
                  );
                }
                const r = item.row;
                return (
                  <Tr
                    key={`co-${r.companyId}`}
                    cursor="pointer"
                    onClick={() => openDrilldown(r)}
                    _hover={{ bg: hoverBg }}
                  >
                    <Td pl={item.inOrg ? 10 : undefined}>
                      <Text fontWeight="500">{r.companyName}</Text>
                      <Text fontSize="xs" color="gray.500">
                        #{r.companyId}
                      </Text>
                    </Td>
                    {renderMetricCells(r)}
                  </Tr>
                );
              })}
              {display.length === 0 && (
                <Tr>
                  <Td colSpan={8} textAlign="center" py={10}>
                    <Text color="gray.500">אין נתונים</Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Box>
      )}

      {rows.length > 0 && (
        <Text fontSize="xs" color="gray.500" mt={3}>
          נתונים מתעדכנים אוטומטית מדי לילה בשעה 03:30 (שעון ישראל). לחץ על שורת חברה לפירוט + היסטוריית 90 יום.
          דלתות (±N) מוצגות כאשר יש היסטוריה של ≥7 ימים.
        </Text>
      )}

      <EmployeeStatusDrilldownModal
        isOpen={isOpen}
        onClose={() => {
          onClose();
          setSelectedCompany(null);
        }}
        company={selectedCompany}
      />
    </Box>
  );
}
